import { useEffect } from "react";
import "@/App.css";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import LoadingScreen from "@/components/site/LoadingScreen";
import CustomCursor from "@/components/site/CustomCursor";
import Navbar from "@/components/site/Navbar";
import SideNav from "@/components/site/SideNav";
import Hero from "@/components/site/Hero";
import Marquee from "@/components/site/Marquee";
import About from "@/components/site/About";
import Services from "@/components/site/Services";
import Brokerage from "@/components/site/Brokerage";
import Network from "@/components/site/Network";
import Portfolio from "@/components/site/Portfolio";
import Testimonials from "@/components/site/Testimonials";
import Process from "@/components/site/Process";
import Pricing from "@/components/site/Pricing";
import Careers from "@/components/site/Careers";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import Login from "@/components/site/Login";
import Track from "@/components/site/Track";
import AuthCallback from "@/components/site/AuthCallback";
import ProtectedRoute from "@/components/site/ProtectedRoute";
import AdminRoute from "@/components/site/AdminRoute";
import Blog from "@/components/site/Blog";
import BlogPost from "@/components/site/BlogPost";
import Admin from "@/components/site/Admin";
import DriverApp from "@/components/site/DriverApp";
import FloatingActions from "@/components/site/FloatingActions";
import { LanguageProvider } from "@/context/LanguageContext";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return <motion.div data-testid="scroll-progress" style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[3px] origin-left bg-primary z-[90]" />;
}

function Landing() {
  useEffect(() => {
    // Smooth-scroll only on precise pointers; touch devices keep snappy native scroll.
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="cursor-none">
      <CustomCursor />
      <LoadingScreen />
      <ScrollProgress />
      <Navbar />
      <SideNav />
      <main>
        <Hero />
        <Marquee />
        <About />
        <Services />
        <Brokerage />
        <Portfolio />
        <Network />
        <Testimonials />
        <Process />
        <Pricing />
        <Contact />
        <Careers />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/track" element={<ProtectedRoute><Track /></ProtectedRoute>} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="/driver" element={<DriverApp />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <LanguageProvider>
      <AuthProvider>
        <div className="App grain min-h-screen bg-background text-foreground">
          <BrowserRouter>
            <AppRouter />
            <FloatingActions />
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </div>
      </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
